// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/postToProjectSharedFeed.test.ts
import { type SendAskPunttMessageResponse } from "@mg/schemas/src/christo/askPuntt";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useUI } from "../../contexts/ui";
import * as AskPunttService from "../../services/askPuntt";
import * as ProjectsService from "../../services/askPunttProjects";
import {
  appendSendResultToChatHistory,
  askPunttKeys,
} from "../../utils/queries/askPuntt";
import { askPunttProjectKeys } from "../../utils/queries/askPunttProjects";

export type PostToProjectSharedFeedInput = {
  projectId: string;
  sharedChatId?: string | null;
  message: string;
  attachments?: {
    key: string;
    source: string;
    type: string;
    name?: string;
  }[];
  reviewMode?: "standard" | "deep";
};

export type PostToProjectSharedFeedResult = {
  sharedChatId: string;
  sendResult: SendAskPunttMessageResponse;
};

/**
 * Append a home Project-mode turn onto the project's existing shared feed
 * chat. Never creates a personal chat. If the feed already has messages
 * (starter exchange, prior turns), sendMessage adds this turn at the end.
 */
export async function postToProjectSharedFeed(
  input: PostToProjectSharedFeedInput,
): Promise<PostToProjectSharedFeedResult> {
  let sharedChatId =
    input.sharedChatId != null && input.sharedChatId !== ""
      ? input.sharedChatId
      : null;
  if (sharedChatId == null) {
    const projectResult = await ProjectsService.getProject(input.projectId);
    const resolved = projectResult.project.sharedChatId;
    sharedChatId = resolved != null && resolved !== "" ? resolved : null;
  }
  if (sharedChatId == null) {
    throw new Error(`Project ${input.projectId} has no shared feed chat`);
  }

  const sendResult = await AskPunttService.sendMessage(sharedChatId, {
    message: input.message,
    attachments: input.attachments,
    reviewMode: input.reviewMode,
  });
  return { sharedChatId, sendResult };
}

/** Home Project-mode submit: post to the shared feed and refresh timeline cache. */
export function usePostToProjectSharedFeed() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: postToProjectSharedFeed,
    onSuccess: (data, vars) => {
      queryClient.setQueryData(
        askPunttKeys.messages(data.sharedChatId),
        (
          old:
            | Awaited<ReturnType<typeof AskPunttService.getChatHistory>>
            | undefined,
        ) =>
          old != null
            ? appendSendResultToChatHistory(old, data.sendResult)
            : old,
      );
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.chatsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.chat(data.sharedChatId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(vars.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(vars.projectId),
      });
    },
    onError: (error) => {
      console.error("Failed to post to project shared feed:", error);
      notify({
        title: "Failed to post",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}
